import { useState, useEffect, useCallback } from 'react';

import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { useCartContext } from '@/services/cart/CartContext';

import journeysData from './journeys.json';
import { JourneyOptions, JourneyName } from './types';
import { buildJourneyOptions } from './utils';
import { setJourney } from './journeysSlice';
import { GTM_EVENTS, setGtmEventChange } from '../gtm/gtmSlice';

export function useJourneyOptions() {
  const dispatch = useAppDispatch();
  const selectedJourney = useAppSelector(state => state.journeys.journey);

  const [journeyOptions, setJourneyOptions] = useState<JourneyOptions>();

  const {
    getCartAvailableJourneys, getCartDefaultJourney, cartData, cartId
  } = useCartContext();

  // Build journey options based on current cart
  useEffect(() => {
    const cartAvailableJourneys = getCartAvailableJourneys();

    if (!cartAvailableJourneys) {
      return;
    }

    const newJourneyOptions = buildJourneyOptions(journeysData, cartAvailableJourneys);
    if (!newJourneyOptions) {
      logger.error('Problem setting journeys from cart data!');
      return;
    }

    setJourneyOptions(newJourneyOptions);
  }, [getCartAvailableJourneys, dispatch]);

  // Set default journey based on current cart
  useEffect(() => {
    // Wait until journeyOptions are set
    if (!journeyOptions) {
      return;
    }

    // Skip setting default journey if selected journey is already set (and is part of current journey options)
    if (selectedJourney && (selectedJourney in journeyOptions)) {
      return;
    }

    // Never override the 'direct' journey - it's managed separately from cart-based journeys
    if (selectedJourney === 'direct') {
      return;
    }

    const cartDefaultJourney = getCartDefaultJourney() as JourneyName;

    if (!cartDefaultJourney) {
      return;
    }

    dispatch(setJourney(cartDefaultJourney));
  }, [getCartDefaultJourney, selectedJourney, journeyOptions, dispatch]);

  const handleJourneySelect = useCallback((journey: string) => {
    dispatch(setJourney(journey as JourneyName));

    // Let GTM know we've changed journey type
    if (cartId && cartData) {
      dispatch(setGtmEventChange({ eventType: GTM_EVENTS.FRONT_PAGE_CHANGE, cartId, cartData }));
    }
  }, [dispatch, cartId, cartData]);

  return {
    journeyOptions,
    selectedJourney,
    handleJourneySelect,
  };
}
